import { useState, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  AlertCircle, Search, RotateCcw, ChevronLeft, ChevronRight,
  Loader2, MapPin, Calendar, X, Building2,
  FileText, Clock, Download, Hospital, Activity, Users, Image,
  AlertTriangle, CheckCircle2, ExternalLink, ArrowUpDown
} from 'lucide-react';
import * as XLSX from 'xlsx';
import useDogBiteReports from '../hooks/useDogBiteReports';
import useUnionCouncils from '../hooks/useUnionCouncils';
import { useAuth } from '../context/AuthContext';
import { DISTRICTS, getTehsilsForDistrict, getDistrictsForDivision } from '../constants';

const DogBiteReports = () => {
  const { darkMode } = useOutletContext();
  const { adminScope } = useAuth();

  // Scope-aware options
  const isDistrictLocked = adminScope?.accessLevel === 'district' || adminScope?.accessLevel === 'tehsil';
  const isTehsilLocked = adminScope?.accessLevel === 'tehsil';

  // Scope-aware districts
  const scopeDistricts = useMemo(() => {
    if (!adminScope) return DISTRICTS;
    if (adminScope.accessLevel === 'tehsil' && adminScope.district) return [adminScope.district];
    if (adminScope.accessLevel === 'district' && adminScope.district) return [adminScope.district];
    if (adminScope.accessLevel === 'division' && adminScope.division) return getDistrictsForDivision(adminScope.division);
    return DISTRICTS; // province
  }, [adminScope]);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDistrict, setFilterDistrict] = useState(adminScope?.district || '');
  const [filterTahsil, setFilterTahsil] = useState(adminScope?.tehsil || '');
  const [filterUc, setFilterUc] = useState('');
  const [filterSex, setFilterSex] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Available tehsils based on selected district
  const scopeTehsils = useMemo(() => {
    if (isTehsilLocked && adminScope?.tehsil) return [adminScope.tehsil];
    if (filterDistrict) return getTehsilsForDistrict(filterDistrict);
    return scopeDistricts.flatMap(d => getTehsilsForDistrict(d));
  }, [filterDistrict, adminScope, isTehsilLocked, scopeDistricts]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal states
  const [showHospitalModal, setShowHospitalModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [hospitalDetails, setHospitalDetails] = useState(null);
  const [hospitalLoading, setHospitalLoading] = useState(false);

  // Bite Incident Modal states
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);

  const filters = useMemo(() => ({
    district: filterDistrict,
    tahsil: filterTahsil,
    uc: filterUc,
    sex: filterSex,
    status: filterStatus,
    startDate,
    endDate,
    searchQuery
  }), [filterDistrict, filterTahsil, filterUc, filterSex, filterStatus, startDate, endDate, searchQuery]);

  // Union councils for filter (scope-aware)
  const { data: unionCouncilsList } = useUnionCouncils({ district: filterDistrict, tahsil: filterTahsil });

  // Filter UCs based on selected Tehsil
  const availableUCs = useMemo(() => {
    let ucs = unionCouncilsList || [];
    if (filterTahsil) {
      ucs = ucs.filter(uc => uc.tahsil === filterTahsil);
    }
    // Sort by UC Number
    return [...ucs].sort((a, b) => {
      const numA = parseInt((a.uc || a.name || '').replace(/\D/g, '')) || 0;
      const numB = parseInt((b.uc || b.name || '').replace(/\D/g, '')) || 0;
      return numA - numB;
    });
  }, [unionCouncilsList, filterTahsil]);

  const { data: reports, loading, error, fetchHospitalById, refetch } = useDogBiteReports(filters);
  const [refreshing, setRefreshing] = useState(false);
  const [sortBy, setSortBy] = useState('date_desc'); // sorting option

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    refetch();
    setTimeout(() => setRefreshing(false), 500);
  };

  // Sorted and paginated reports
  const sortedReports = useMemo(() => {
    return [...reports].sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.incidentDateTime || b.createdAt || 0) - new Date(a.incidentDateTime || a.createdAt || 0);
        case 'date_asc':
          return new Date(a.incidentDateTime || a.createdAt || 0) - new Date(b.incidentDateTime || b.createdAt || 0);
        case 'name':
          return (a.patientName || '').localeCompare(b.patientName || '');
        case 'age':
          return (parseInt(a.patientAge) || 0) - (parseInt(b.patientAge) || 0);
        case 'tehsil':
          return (a.tahsil || '').localeCompare(b.tahsil || '');
        case 'status':
          return (a.actionTaken === b.actionTaken) ? 0 : a.actionTaken ? -1 : 1;
        default:
          return 0;
      }
    });
  }, [reports, sortBy]);

  // Paginated reports
  const paginatedReports = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedReports.slice(startIndex, startIndex + pageSize);
  }, [sortedReports, currentPage, pageSize]);

  // Total pages
  const totalPages = Math.ceil(reports.length / pageSize);

  // Stats
  const stats = useMemo(() => ({
    total: reports.length,
    male: reports.filter(r => r.patientSex === 'Male').length,
    female: reports.filter(r => r.patientSex === 'Female').length,
    actionTaken: reports.filter(r => r.actionTaken).length,
    actionRequired: reports.filter(r => !r.actionTaken).length
  }), [reports]);

  // Excel Export Function
  const handleExportToExcel = () => {
    const excelData = reports.map((report, index) => ({
      'S.No': index + 1,
      'Patient Name': report.patientName || 'N/A',
      'Age': report.patientAge || 'N/A',
      'Sex': report.patientSex || 'N/A',
      'Description': report.description || 'N/A',
      'Place of Incident': report.placeOfIncident || 'N/A',
      'Tehsil': report.tahsil || 'N/A',
      'UC Type': report.ucMcType || 'N/A',
      'UC Number': report.ucMcNumber || 'N/A',
      'Action Taken': report.actionTaken ? 'Yes' : 'No',
      'Patient Phone': report.patientPhone || 'N/A',
      'Incident Date': report.incidentDateTime ? new Date(report.incidentDateTime).toLocaleString() : 'N/A',
      'Reported Date': report.createdAt ? new Date(report.createdAt).toLocaleString() : 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    ws['!cols'] = [
      { wch: 8 }, { wch: 20 }, { wch: 8 }, { wch: 10 },
      { wch: 35 }, { wch: 30 }, { wch: 15 }, { wch: 12 },
      { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 20 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dog Bite Reports');

    const date = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Dog_Bite_Reports_${date}.xlsx`);
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchQuery('');
    if (!isDistrictLocked) setFilterDistrict(adminScope?.district || '');
    if (!isTehsilLocked) setFilterTahsil(adminScope?.tehsil || '');
    setFilterUc('');
    setFilterSex('');
    setFilterStatus('');
    setStartDate('');
    setEndDate('');
    setSortBy('date_desc');
    setCurrentPage(1);
  };

  const hasActiveFilters = searchQuery || (!isDistrictLocked && filterDistrict) || (!isTehsilLocked && filterTahsil) || filterUc || filterSex || filterStatus || startDate || endDate;

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle hospital info click
  const handleHospitalInfoClick = useCallback(async (report) => {
    setSelectedReport(report);
    setShowHospitalModal(true);
    setHospitalLoading(true);
    setHospitalDetails(null);

    if (report.submittedBy) {
      const hospital = await fetchHospitalById(report.submittedBy);
      setHospitalDetails(hospital);
    }
    setHospitalLoading(false);
  }, [fetchHospitalById]);

  // Close modal
  const closeModal = () => {
    setShowHospitalModal(false);
    setSelectedReport(null);
    setHospitalDetails(null);
  };

  // Handle incident details click
  const handleIncidentClick = useCallback((report) => {
    setSelectedIncident(report);
    setShowIncidentModal(true);
  }, []);

  // Close incident modal
  const closeIncidentModal = () => {
    setShowIncidentModal(false);
    setSelectedIncident(null);
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Dog Bite Reports</h1>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>View bite incident reports from hospitals</p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleExportToExcel}
              disabled={reports.length === 0}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${reports.length === 0
                ? 'opacity-50 cursor-not-allowed bg-gray-300 text-gray-500'
                : darkMode
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-green-600 text-white hover:bg-green-700'
                }`}
            >
              <Download size={18} />
              Export to Excel
            </button>

            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className={`p-2.5 rounded-lg transition-all ${darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                } ${(refreshing || loading) ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Refresh data"
            >
              <RotateCcw size={18} className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                <Activity size={20} className={darkMode ? 'text-blue-400' : 'text-blue-600'} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stats.total}</p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Reports</p>
              </div>
            </div>
          </div>

          <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-sky-500/20' : 'bg-sky-100'}`}>
                <Users size={20} className={darkMode ? 'text-sky-400' : 'text-sky-600'} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${darkMode ? 'text-sky-400' : 'text-sky-600'}`}>{stats.male}</p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Male Patients</p>
              </div>
            </div>
          </div>

          <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-pink-500/20' : 'bg-pink-100'}`}>
                <Users size={20} className={darkMode ? 'text-pink-400' : 'text-pink-600'} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${darkMode ? 'text-pink-400' : 'text-pink-600'}`}>{stats.female}</p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Female Patients</p>
              </div>
            </div>
          </div>

          <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-red-500/20' : 'bg-red-100'}`}>
                <AlertTriangle size={20} className={darkMode ? 'text-red-400' : 'text-red-600'} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>{stats.actionRequired}</p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Action Required</p>
              </div>
            </div>
          </div>

          <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
                <CheckCircle2 size={20} className={darkMode ? 'text-green-400' : 'text-green-600'} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{stats.actionTaken}</p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Action Taken</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={`p-4 rounded-xl border mb-6 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <input
                    type="text"
                    placeholder="Search by patient name, place, or description..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className={`w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm ${darkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                      }`}
                  />
                </div>
              </div>

              <div className="w-full lg:w-40">
                <select
                  value={filterDistrict}
                  onChange={(e) => {
                    if (!isDistrictLocked) {
                      setFilterDistrict(e.target.value);
                      setFilterTahsil('');
                      setFilterUc('');
                      setCurrentPage(1);
                    }
                  }}
                  disabled={isDistrictLocked}
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm ${darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-gray-50 border-gray-200 text-gray-900'
                    } ${isDistrictLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <option value="">All Districts</option>
                  {scopeDistricts.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              <div className="w-full lg:w-40">
                <select
                  value={filterTahsil}
                  onChange={(e) => {
                    setFilterTahsil(e.target.value);
                    setFilterUc(''); // Reset UC when Tehsil changes
                    setCurrentPage(1);
                  }}
                  disabled={isTehsilLocked}
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm ${darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-gray-50 border-gray-200 text-gray-900'
                    } ${isTehsilLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <option value="">All Tehsils</option>
                  {scopeTehsils.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div className="w-full lg:w-44">
                <select
                  value={filterUc}
                  onChange={(e) => {
                    setFilterUc(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm ${darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-gray-50 border-gray-200 text-gray-900'
                    }`}
                >
                  <option value="">All UCs</option>
                  {availableUCs.map(uc => (
                    <option key={uc.uid || uc.id} value={uc.uc}>
                      {uc.uc}
                    </option>
                  ))}
                </select>
              </div>

              <div className="w-full lg:w-40">
                <select
                  value={filterSex}
                  onChange={(e) => {
                    setFilterSex(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm ${darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-gray-50 border-gray-200 text-gray-900'
                    }`}
                >
                  <option value="">All Genders</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>

              <div className="w-full lg:w-44">
                <select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm ${darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-gray-50 border-gray-200 text-gray-900'
                    }`}
                >
                  <option value="">All Status</option>
                  <option value="actionRequired">Action Required</option>
                  <option value="actionTaken">Action Taken</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="w-full sm:w-44">
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  From Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm ${darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-gray-50 border-gray-200 text-gray-900'
                    }`}
                />
              </div>

              <div className="w-full sm:w-44">
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  To Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm ${darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-gray-50 border-gray-200 text-gray-900'
                    }`}
                />
              </div>

              {hasActiveFilters && (
                <button
                  onClick={handleResetFilters}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${darkMode
                    ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  <RotateCcw size={14} />
                  Reset
                </button>
              )}

              {/* Sort Dropdown */}
              <div className="relative">
                <ArrowUpDown size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className={`h-[42px] pl-8 pr-3 rounded-lg border text-sm appearance-none cursor-pointer ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-100 border-gray-200 text-gray-900'}`}
                  title="Sort by"
                >
                  <option value="date_desc">Newest</option>
                  <option value="date_asc">Oldest</option>
                  <option value="name">Name</option>
                  <option value="age">Age</option>
                  <option value="tehsil">Tehsil</option>
                  <option value="status">Status</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-blue-600" size={40} />
            <span className={`ml-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Loading reports...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className={`p-4 rounded-xl border ${darkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center gap-3">
              <AlertCircle className={darkMode ? 'text-red-400' : 'text-red-600'} size={20} />
              <p className={darkMode ? 'text-red-200' : 'text-red-800'}>Error: {error}</p>
            </div>
          </div>
        )}

        {/* Reports Table */}
        {!loading && !error && (
          <div className={`rounded-xl border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Patient
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Description
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Location
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      UC Details
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Status
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Incident Date
                    </th>
                    <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                  {paginatedReports.length > 0 ? (
                    paginatedReports.map((report) => (
                      <tr
                        key={report.id}
                        className={`transition-colors ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm ${report.patientSex === 'Male' ? 'bg-sky-600' : 'bg-pink-600'
                              }`}>
                              {report.patientName?.[0]?.toUpperCase() || 'P'}
                            </div>
                            <div>
                              <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {report.patientName || 'N/A'}
                              </p>
                              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {report.patientSex || 'N/A'}, {report.patientAge || 'N/A'} years
                              </p>
                              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {report.patientPhone || 'N/A'}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-4 max-w-[250px]">
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} line-clamp-2`} title={report.description}>
                            {report.description || 'N/A'}
                          </p>
                        </td>

                        <td className="px-4 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Building2 size={12} className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{report.tahsil || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin size={12} className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} truncate max-w-[150px]`} title={report.placeOfIncident}>
                                {report.placeOfIncident || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-4">
                          <div className="space-y-1">
                            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {report.ucMcNumber || 'N/A'}
                            </p>
                          </div>
                        </td>

                        <td className="px-4 py-4">
                          <span
                            onClick={report.actionTaken ? () => handleIncidentClick(report) : undefined}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${report.actionTaken
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 cursor-pointer hover:bg-green-200 dark:hover:bg-green-900/50 transition-all'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              }`}>
                            {report.actionTaken ? 'Action Taken' : 'Action Required'}
                          </span>
                        </td>

                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <Calendar size={12} className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {formatDate(report.incidentDateTime)}
                            </span>
                          </div>
                        </td>

                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() => handleHospitalInfoClick(report)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${darkMode
                                ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                                : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                }`}
                            >
                              <FileText size={14} />
                              View Details
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-4 py-12 text-center">
                        <Activity size={40} className={`mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                        <p className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No reports found</p>
                        <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {hasActiveFilters ? 'Try adjusting your filters' : 'No bite incident reports have been submitted yet'}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {reports.length > 0 && (
              <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, reports.length)} of {reports.length} reports
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className={`px-2 py-1.5 rounded-lg border text-sm ${darkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-200 text-gray-900'
                      }`}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-lg transition-all ${currentPage === 1
                        ? 'opacity-50 cursor-not-allowed'
                        : darkMode
                          ? 'hover:bg-gray-700 text-gray-400'
                          : 'hover:bg-gray-100 text-gray-600'
                        }`}
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <span className={`px-3 py-1.5 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {currentPage} / {totalPages || 1}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className={`p-2 rounded-lg transition-all ${currentPage === totalPages || totalPages === 0
                        ? 'opacity-50 cursor-not-allowed'
                        : darkMode
                          ? 'hover:bg-gray-700 text-gray-400'
                          : 'hover:bg-gray-100 text-gray-600'
                        }`}
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Hospital Information Modal */}
        {showHospitalModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeModal} />
            <div className={`relative w-full max-w-lg rounded-2xl shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                    <FileText size={20} className={darkMode ? 'text-blue-400' : 'text-blue-600'} />
                  </div>
                  <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Patient Info
                  </h2>
                </div>
                <button onClick={closeModal} className={`p-2 rounded-lg transition-all ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 sm:p-6 max-h-[70vh] overflow-y-auto">
                {hospitalLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="animate-spin text-blue-600" size={32} />
                    <span className={`ml-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Loading details...</span>
                  </div>
                ) : selectedReport ? (
                  <div className="space-y-4">
                    <div className={`flex items-center gap-4 pb-4 border-b border-dashed ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-xl">
                        <Hospital size={32} />
                      </div>
                      <div>
                        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {hospitalDetails?.hospitalName || 'N/A'}
                        </h3>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {hospitalDetails?.tahsil || 'N/A'} Tehsil
                        </p>
                      </div>
                    </div>

                    {selectedReport && (
                      <div className={`mt-4 pt-4 border-t border-dashed ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <FileText size={16} />
                          Incident & Patient Details
                        </h4>
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} space-y-3`}>
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Patient Information</p>
                            <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {selectedReport.patientName}, {selectedReport.patientSex}, {selectedReport.patientAge} years
                              {selectedReport.patientPhone && (
                                <span className="block">{selectedReport.patientPhone}</span>
                              )}
                            </p>
                          </div>
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Patient Address</p>
                            <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {selectedReport.patientAddress || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Description</p>
                            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {selectedReport.description || 'No description provided'}
                            </p>
                          </div>
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Place of Incident</p>
                            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {selectedReport.placeOfIncident || 'N/A'}
                            </p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Incident Date</p>
                              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {formatDate(selectedReport.incidentDateTime)}
                              </p>
                            </div>
                            <div>
                              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Reported Date</p>
                              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {formatDate(selectedReport.createdAt)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Hospital size={48} className={`mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                    <p className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Hospital not found</p>
                    <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      The hospital information could not be retrieved
                    </p>
                  </div>
                )}
              </div>

              <div className={`flex items-center justify-end p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={closeModal}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${darkMode
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bite Incident Details Modal */}
        {showIncidentModal && selectedIncident && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeIncidentModal} />
            <div className={`relative w-full max-w-2xl rounded-2xl shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
                    <Activity size={20} className={darkMode ? 'text-amber-400' : 'text-amber-600'} />
                  </div>
                  <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Bite Incident Details
                  </h2>
                </div>
                <button onClick={closeIncidentModal} className={`p-2 rounded-lg transition-all ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 sm:p-6 max-h-[70vh] overflow-y-auto">
                <div className="space-y-4">
                  {/* Action Status */}
                  <div className={`flex items-center justify-between p-4 rounded-xl border ${darkMode ? 'bg-gray-700/30 border-gray-700' : 'bg-green-50 border-green-200'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
                        <Activity size={20} className={darkMode ? 'text-green-400' : 'text-green-600'} />
                      </div>
                      <div>
                        <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Action Taken</p>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {selectedIncident.actionTime ? formatDate(selectedIncident.actionTime) : 'Confirmed'}
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      ✓ Completed
                    </span>
                  </div>

                  {/* Incident Image */}
                  {selectedIncident.imageUrl && (
                    <div className={`rounded-xl border overflow-hidden ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <div className={`p-3 border-b ${darkMode ? 'border-gray-700 bg-gray-700/30' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="flex items-center gap-2">
                          <Image size={16} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                          <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Incident Photo</p>
                        </div>
                      </div>
                      <div className="p-2">
                        <img
                          src={selectedIncident.imageUrl}
                          alt="Bite Incident"
                          className="w-full h-auto rounded-lg object-cover max-h-72"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-48 text-gray-400"><p>Image unavailable</p></div>';
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* UC Information */}
                  <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-700/30 border-gray-700' : 'bg-purple-50 border-purple-200'}`}>
                    <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <Building2 size={16} />
                      Union Council Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {selectedIncident.ucMcType && (
                        <div>
                          <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>UC/MC Type</p>
                          <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            {selectedIncident.ucMcType}
                          </p>
                        </div>
                      )}

                      {selectedIncident.ucMcNumber && (
                        <div>
                          <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>UC/MC Number</p>
                          <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            {selectedIncident.ucMcNumber}
                          </p>
                        </div>
                      )}

                      {selectedIncident.tahsil && (
                        <div>
                          <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Tehsil</p>
                          <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            {selectedIncident.tahsil}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Incident Information Grid */}
                  <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-700/30 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                    <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <FileText size={16} />
                      Incident Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {selectedIncident.description && (
                        <div className="sm:col-span-2">
                          <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Description</p>
                          <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            {selectedIncident.description}
                          </p>
                        </div>
                      )}

                      {selectedIncident.dogType && (
                        <div>
                          <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Dog Type</p>
                          <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            {selectedIncident.dogType}
                          </p>
                        </div>
                      )}

                      {selectedIncident.hospitalName && (
                        <div>
                          <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Hospital</p>
                          <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            {selectedIncident.hospitalName}
                          </p>
                        </div>
                      )}

                      {selectedIncident.coordinates && (
                        <div className="sm:col-span-2">
                          <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Location Coordinates</p>
                          <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2">
                              <MapPin size={14} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                              <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                Lat: {selectedIncident.coordinates.lat?.toFixed(6) || 'N/A'}, Lng: {selectedIncident.coordinates.lng?.toFixed(6) || 'N/A'}
                              </p>
                            </div>
                            {selectedIncident.coordinates.lat && selectedIncident.coordinates.lng && (
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${selectedIncident.coordinates.lat},${selectedIncident.coordinates.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all ${darkMode
                                  ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                                  : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                  }`}
                              >
                                <ExternalLink size={12} />
                                Open in Map
                              </a>
                            )}
                          </div>
                        </div>
                      )}

                      {selectedIncident.createdAt && (
                        <div>
                          <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Reported At</p>
                          <div className="flex items-center gap-2">
                            <Clock size={14} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                            <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                              {formatDate(selectedIncident.createdAt)}
                            </p>
                          </div>
                        </div>
                      )}

                      {selectedIncident.actionTime && (
                        <div>
                          <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Action Taken At</p>
                          <div className="flex items-center gap-2">
                            <Clock size={14} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                            <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                              {formatDate(selectedIncident.actionTime)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Patient Information */}
                  <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-700/30 border-gray-700' : 'bg-blue-50 border-blue-200'}`}>
                    <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <Users size={16} />
                      Patient Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Patient Name</p>
                        <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          {selectedIncident.patientName || 'N/A'}
                        </p>
                      </div>

                      <div>
                        <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Age & Gender</p>
                        <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          {selectedIncident.patientAge || 'N/A'} years, {selectedIncident.patientSex || 'N/A'}
                        </p>
                      </div>

                      <div>
                        <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Patient Phone</p>
                        <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          {selectedIncident.patientPhone || 'N/A'}
                        </p>
                      </div>

                      <div className="sm:col-span-2">
                        <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Patient Address</p>
                        <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          {selectedIncident.patientAddress || 'N/A'}
                        </p>
                      </div>

                      <div className="sm:col-span-2">
                        <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Place of Incident</p>
                        <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          {selectedIncident.placeOfIncident || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>


                </div>
              </div>

              <div className={`flex items-center justify-end p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={closeIncidentModal}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${darkMode
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DogBiteReports;